/**
 * StreamSlice — append-only transcript buffer (turns + tokens) under immer.
 * Invariants: tokens are append-only per turn; finalText set exactly once at endTurn;
 * outcome ∈ {spoke, forfeited} — never null after endTurn.
 */
import type { StateCreator } from "zustand";
import type {
  CitationAudit,
  Critique,
  RagCitation,
  ResearchSource,
  Turn,
  TurnOutcome,
  TurnPhase,
} from "../../domain/types";
import type { DebateStore } from "../debateStore";

export interface AttachResearchInput {
  query: string;
  sources: ResearchSource[];
  elapsedMs: number;
  cacheHit?: boolean;
}

export interface AttachRagBatchInput {
  query: string;
  citations: RagCitation[];
}

export type StartTurnInput = Omit<
  Turn,
  | "tokens"
  | "finalText"
  | "endedAt"
  | "audioChunkIds"
  | "outcome"
  | "forfeitReason"
  | "startedAt"
> & {
  startedAt?: number;
};

export interface StreamSlice {
  currentSpeakerId: string | null;
  currentTurnId: string | null;
  /**
   * Most-recent speaker, kept across turn-end → next-turn-start gaps so
   * the Roundtable doesn't flash "SESSİZLİK" while audio is still playing
   * out the just-ended turn or the engine is preparing the next one.
   * Cleared only on `resetStream()`.
   */
  previousSpeakerId: string | null;
  turnsById: Record<string, Turn>;
  turnOrder: string[];
  roundIndex: number;

  startTurn(turn: StartTurnInput): void;
  appendToken(turnId: string, token: string): void;
  setTurnPhase(turnId: string, phase: TurnPhase): void;
  attachCritique(turnId: string, critique: Critique): void;
  endTurn(
    turnId: string,
    finalText: string,
    outcome: TurnOutcome,
    forfeitReason?: string,
    citationAudit?: CitationAudit | null,
  ): void;
  attachResearch(turnId: string, input: AttachResearchInput): void;
  /**
   * Attach a RAG-citation batch to a turn (idempotent at the
   * (turnId, query) level — duplicate batches from event replay are
   * coalesced so the verdict bibliography never double-counts).
   */
  attachRagBatch(turnId: string, input: AttachRagBatchInput): void;
  /**
   * Attach the engine-bounded reasoning summary surfaced by
   * `turn.thinking`. Idempotent: re-emitted thinking events overwrite
   * (the engine sends at most one per turn, but stream replays can
   * deliver it twice).
   */
  attachThinking(turnId: string, payload: { chars: number; summary: string }): void;
  bumpRound(roundIndex: number): void;
  resetStream(): void;
}

export type StreamSliceCreator = StateCreator<
  DebateStore,
  [["zustand/immer", never]],
  [],
  StreamSlice
>;

export const createStreamSlice: StreamSliceCreator = (set) => ({
  currentSpeakerId: null,
  currentTurnId: null,
  previousSpeakerId: null,
  turnsById: {},
  turnOrder: [],
  roundIndex: 0,

  startTurn: (input) => {
    set((state) => {
      const startedAt = input.startedAt ?? Date.now();
      const turn: Turn = {
        id: input.id,
        speakerId: input.speakerId,
        roundIndex: input.roundIndex,
        phase: input.phase,
        critique: input.critique,
        tokens: [],
        finalText: null,
        startedAt,
        endedAt: null,
        audioChunkIds: [],
        outcome: null,
      };
      state.turnsById[input.id] = turn;
      if (!state.turnOrder.includes(input.id)) {
        state.turnOrder.push(input.id);
      }
      // Promote the outgoing speaker to "previous" before swapping in the
      // new active one. This keeps the stage continuously showing SOMEONE
      // across turn boundaries so the audience never sees a SESSİZLİK flash.
      if (state.currentSpeakerId && state.currentSpeakerId !== input.speakerId) {
        state.previousSpeakerId = state.currentSpeakerId;
      }
      state.currentTurnId = input.id;
      state.currentSpeakerId = input.speakerId;
      if (input.roundIndex > state.roundIndex) {
        state.roundIndex = input.roundIndex;
      }
    });
  },

  appendToken: (turnId, token) => {
    set((state) => {
      const t = state.turnsById[turnId];
      if (!t) return;
      // Critique-First guard: silently drop tokens that arrive before a critique
      // has been attached. The backend SHOULD prevent this — defence-in-depth.
      if (!t.critique) return;
      t.tokens.push(token);
      if (t.phase !== "speaking") {
        t.phase = "speaking";
      }
    });
  },

  setTurnPhase: (turnId, phase) => {
    set((state) => {
      const t = state.turnsById[turnId];
      if (!t) return;
      t.phase = phase;
    });
  },

  attachCritique: (turnId, critique) => {
    set((state) => {
      const t = state.turnsById[turnId];
      if (!t) return;
      t.critique = { ...critique, targetTurnId: critique.targetTurnId || turnId };
    });
  },

  endTurn: (turnId, finalText, outcome, forfeitReason, citationAudit) => {
    set((state) => {
      const t = state.turnsById[turnId];
      if (!t) return;
      t.finalText = finalText;
      t.endedAt = Date.now();
      t.outcome = outcome;
      t.phase = "yielding";
      if (typeof forfeitReason === "string" && forfeitReason.length > 0) {
        t.forfeitReason = forfeitReason;
      }
      if (citationAudit) {
        t.citationAudit = citationAudit;
      }
      if (state.currentTurnId === turnId) {
        // Hand the lamp off to "previous" so the stage keeps a face up
        // until the next speaker takes over (no SESSİZLİK flash).
        state.previousSpeakerId = state.currentSpeakerId;
        state.currentTurnId = null;
        state.currentSpeakerId = null;
      }
    });
  },

  attachResearch: (turnId, input) => {
    set((state) => {
      const t = state.turnsById[turnId];
      if (!t) return;
      if (!t.research) t.research = [];
      t.research.push({
        query: input.query,
        sources: input.sources,
        elapsedMs: input.elapsedMs,
        cacheHit: Boolean(input.cacheHit),
        receivedAt: Date.now(),
      });
    });
  },

  attachRagBatch: (turnId, input) => {
    set((state) => {
      const t = state.turnsById[turnId];
      if (!t) return;
      if (!t.ragBatches) t.ragBatches = [];
      // Coalesce: a `turn.end` carrying the same `ragCitations` as the
      // earlier `turn.rag` event must NOT double-count in the verdict
      // bibliography. We dedupe by (query, set-of-docId#chunkIdx).
      const fingerprint = `${input.query}::${input.citations
        .map((c) => `${c.docId}#${c.chunkIdx}`)
        .sort()
        .join(",")}`;
      const exists = t.ragBatches.some(
        (b) =>
          `${b.query}::${b.citations
            .map((c) => `${c.docId}#${c.chunkIdx}`)
            .sort()
            .join(",")}` === fingerprint,
      );
      if (exists) return;
      t.ragBatches.push({
        query: input.query,
        citations: input.citations,
        receivedAt: Date.now(),
      });
    });
  },

  attachThinking: (turnId, payload) => {
    set((state) => {
      const t = state.turnsById[turnId];
      if (!t) return;
      t.thinking = { chars: payload.chars, summary: payload.summary };
    });
  },

  bumpRound: (roundIndex) => {
    set((state) => {
      if (roundIndex > state.roundIndex) {
        state.roundIndex = roundIndex;
      }
    });
  },

  resetStream: () => {
    set((state) => {
      state.currentSpeakerId = null;
      state.currentTurnId = null;
      state.previousSpeakerId = null;
      state.turnsById = {};
      state.turnOrder = [];
      state.roundIndex = 0;
    });
  },
});
